"use client"

import * as React from "react"
import { LexicalEditorWrapper as LexicalEditor } from "@/components/shared/lexical-editor-wrapper"
import type { SerializedEditorState } from "@/components/shared/lexical-editor-wrapper"

/**
 * Checks if the editor state is effectively empty
 */
function isEditorStateEmpty(state: SerializedEditorState | string | null | undefined): boolean {
  if (!state) return true;
  
  let parsed: unknown;
  if (typeof state === "string") {
    try {
      parsed = JSON.parse(state);
    } catch {
      return !state.trim();
    }
  } else {
    parsed = state;
  }
  
  if (!parsed || typeof parsed !== "object") return true;
  
  const stateObj = parsed as { root?: { children?: Array<{ type: string; children?: Array<{ type: string; text?: string }> }> } };
  if (!stateObj.root) return true;
  
  const root = stateObj.root;
  if (!root.children || root.children.length === 0) return true;
  
  if (root.children.length === 1) {
    const firstChild = root.children[0];
    if (firstChild.type === "paragraph") {
      if (!firstChild.children || firstChild.children.length === 0) return true;
      if (firstChild.children.length === 1) {
        const textNode = firstChild.children[0];
        if (textNode.type === "text" && (!textNode.text || !textNode.text.trim())) {
          return true;
        }
      }
    }
  }
  
  return false;
}

export function EditorViewer({
  editorSerializedState,
  fallback = null,
  className,
  id,
  as: Component = "div",
}: {
  editorSerializedState: unknown
  fallback?: React.ReactNode
  className?: string
  id?: string
  as?: React.ElementType
}) {
  const normalizedValue = typeof editorSerializedState === "string" 
    ? (function() {
        try {
          const parsed = JSON.parse(editorSerializedState)
          if (parsed && typeof parsed === "object" && "root" in parsed) return parsed as SerializedEditorState
        } catch { /* ignore */ }
        return null
      })()
    : (editorSerializedState && typeof editorSerializedState === "object" && "root" in (editorSerializedState as Record<string, unknown>)
        ? (editorSerializedState as SerializedEditorState)
        : null)

  // During SSR, we render based on available data. 
  // If data is missing or empty, we show fallback.
  // If data is a plain string, we show the component.
  // If data is Lexical JSON, we show nothing (ssr: false for LexicalEditor) OR a placeholder.
  
  if (!normalizedValue) {
    if (typeof editorSerializedState === "string" && editorSerializedState.trim()) {
      return (
        <Component 
          id={id}
          className={className}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {editorSerializedState}
        </Component>
      )
    }
    return <>{fallback}</>
  }

  if (isEditorStateEmpty(normalizedValue)) return <>{fallback}</>

  return (
    <Component id={id} className={className}>
      <LexicalEditor
        value={normalizedValue}
        readOnly={true}
      />
    </Component>
  )
}
